<template>
  <div class="user-dashboard">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
      </ul>
    </nav>
    <h1 class="title">Dashboard</h1>

    <!-- Broadcast Message Modal -->
    <!-- Broadcast Message Modal -->
    <div v-if="showModal" class="modal is-active">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-content">
        <div class="box has-shadow has-background-light p-5">
          <!-- Modal Title -->
          <h2 class="title is-4 has-text-centered has-text-primary">{{ currentMessage.title }}</h2>
          <!-- Modal Content -->
          <div class="content mt-4">
            <p class="is-size-5 has-text-justified">{{ currentMessage.content }}</p>
          </div>
          <!-- Buttons -->
          <div class="buttons is-centered mt-5">
            <button class="button is-link is-light" @click="dismissMessage">
              <span class="icon"><i class="fas fa-times-circle"></i></span>
              <span>Do Not Show This Again</span>
            </button>
            <button
              v-if="hasMoreMessages"
              class="button is-primary is-light"
              @click="nextMessage"
            >
              <span class="icon"><i class="fas fa-arrow-right"></i></span>
              <span>Next</span>
            </button>
          </div>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="closeModal"></button>
    </div>


    <!-- Tab Navigation -->
    <div class="tabs is-boxed">
      <ul>
        <li :class="{ 'is-active': activeTab === 'overview' }" @click="activeTab = 'overview'">
          <a>Overview</a>
        </li>
        <li :class="{ 'is-active': activeTab === 'graphs' }" @click="activeTab = 'graphs'">
          <a>Graphs</a>
        </li>
      </ul>
    </div>

    <!-- Tab Content -->
    <div v-if="activeTab === 'overview'" class="container">
      <!-- First Row: YTD Counts -->
      <div class="columns is-multiline is-flex is-align-items-stretch">
        <div class="column is-one-quarter">
          <router-link 
            :to="{ 
              path: '/dashboard/invoices', 
              query: { 
                date_start: currentYearStart, 
                date_end: currentYearEnd 
              } 
            }">
            <div class="card equal-height has-background-grey-darker">
              <div class="card-content has-text-white">
                <p class="title is-5 has-text-white">LHDN Submission (YTD)</p>
                <p class="subtitle is-1 has-text-white has-text-right">{{ formatNumber(thisYearValidatedCount) }}</p>
              </div>
            </div>
          </router-link>
        </div>
        <div class="column is-one-quarter">
          <router-link 
            :to="{ 
              path: '/dashboard/invoices', 
              query: { 
                tab: 'Validated', 
                date_start: currentYearStart, 
                date_end: currentYearEnd 
              } 
            }">
            <div class="card equal-height has-background-grey-darker">
              <div class="card-content has-text-white">
                <p class="title is-5 has-text-white pb-4">LHDN Validated (YTD)</p>
                <p class="subtitle is-1 has-text-white has-text-right">{{ formatNumber(thisYearValidatedOnlyCount) }}</p>
              </div>
            </div>
          </router-link>
        </div>
        <div class="column is-one-quarter">
          <router-link 
            :to="{ 
              path: '/dashboard/invoices', 
              query: { 
                tab: 'Submission Failed', 
                date_start: currentYearStart, 
                date_end: currentYearEnd 
              } 
            }">
            <div class="card equal-height has-background-grey-darker">
              <div class="card-content has-text-white">
                <p class="title is-5 has-text-white">Submission Failed (YTD)</p>
                <p class="subtitle is-1 has-text-white has-text-right">{{ formatNumber(thisYearLhdnNotSuccessfulCount) }}</p>
              </div>
            </div>
          </router-link>
        </div>
        <div v-if="canApprove" class="column is-one-quarter">
          <router-link to="/dashboard/invoices/approval">
            <div class="card equal-height has-background-grey-darker">
              <div class="card-content has-text-white">
                <p class="title is-5 has-text-white">Pending Approval (YTD)</p>
                <p class="subtitle is-1 has-text-white has-text-right">{{ formatNumber(thisYearPendingApprovalCount) }}</p>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Second Row: Monthly Counts -->
      <div class="columns is-multiline is-flex is-align-items-stretch">
        <div class="column is-one-quarter">
          <router-link 
            :to="{ 
              path: '/dashboard/invoices', 
              query: { 
                date_start: currentMonthStart, 
                date_end: currentMonthEnd 
              } 
            }">
            <div class="card equal-height has-background-grey-darker">
              <div class="card-content has-text-white">
                <p class="title is-5 has-text-white">LHDN Submission (Monthly)</p>
                <p class="subtitle is-1 has-text-white has-text-right">{{ formatNumber(thisMonthValidatedCount) }}</p>
              </div>
            </div>
          </router-link>
        </div>
        <div class="column is-one-quarter">
          <router-link 
            :to="{ 
              path: '/dashboard/invoices', 
              query: { 
                tab: 'Validated', 
                date_start: currentMonthStart, 
                date_end: currentMonthEnd 
              } 
            }">
            <div class="card equal-height has-background-grey-darker">
              <div class="card-content has-text-white">
                <p class="title is-5 has-text-white">LHDN Validated (Monthly)</p>
                <p class="subtitle is-1 has-text-white has-text-right">{{ formatNumber(thisMonthValidatedOnlyCount) }}</p>
              </div>
            </div>
          </router-link>
        </div>
        <div class="column is-one-quarter">
          <router-link 
            :to="{ 
              path: '/dashboard/invoices', 
              query: { 
                tab: 'Submission Failed', 
                date_start: currentMonthStart, 
                date_end: currentMonthEnd 
              } 
            }">
            <div class="card equal-height has-background-grey-darker">
              <div class="card-content has-text-white">
                <p class="title is-5 has-text-white">Submission Failed (Monthly)</p>
                <p class="subtitle is-1 has-text-white has-text-right">{{ formatNumber(thisMonthLhdnNotSuccessfulCount) }}</p>
              </div>
            </div>
          </router-link>
        </div>
        <div v-if="canApprove" class="column is-one-quarter">
          <router-link to="/dashboard/invoices/approval">
            <div class="card equal-height has-background-grey-darker">
              <div class="card-content has-text-white">
                <p class="title is-5 has-text-white">Pending Approval (Monthly)</p>
                <p class="subtitle is-1 has-text-white has-text-right">{{ formatNumber(thisMonthPendingApprovalCount) }}</p>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Graphs Tab Content -->
    <div v-if="activeTab === 'graphs'" class="container">
      <div class="columns">
        <div class="column is-8">
          <line-chart-component :data="invoicesOverMonths"></line-chart-component>
        </div>
        <div class="column is-4">
          <pie-chart-component :data="topBuyers"></pie-chart-component>
        </div>
      </div>
      <div class="column">
        <bar-chart-component :data="invoicesBySender"></bar-chart-component>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LineChartComponent from '@/components/LineChart.vue';
import BarChartComponent from '@/components/BarChart.vue';
import PieChartComponent from '@/components/PieChart.vue';

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default {
  name: 'UserDashboard',
  components: {
    LineChartComponent,
    BarChartComponent,
    PieChartComponent,
  },
  data() {
    return {
      activeTab: 'overview', // Default tab is 'overview'
      // YTD Counts
      thisYearValidatedCount: 0,
      thisYearValidatedOnlyCount: 0,
      thisYearLhdnNotSuccessfulCount: 0,
      thisYearPendingApprovalCount: 0,
      // Monthly Counts
      thisMonthValidatedCount: 0,
      thisMonthValidatedOnlyCount: 0,
      thisMonthLhdnNotSuccessfulCount: 0,
      thisMonthPendingApprovalCount: 0,
      // Graph Data
      invoicesOverMonths: [],
      topBuyers: [],
      invoicesBySender: [],
      // Broadcast Messages
      messages: [],
      currentIndex: 0,
      showModal: false,
    };
  },
  computed: {
    canApprove() {
      return this.$store.getters.canApproveInvoice;
    },
    canCreateInvoice() {
      return this.$store.getters.canCreateInvoice;
    },
    currentMessage() {
      return this.messages[this.currentIndex] || {};
    },
    hasMoreMessages() {
      return this.currentIndex < this.messages.length - 1;
    },
    currentMonthStart() {
      const now = new Date();
      return formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1));
    },
    currentMonthEnd() {
      const now = new Date();
      return formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    },
    currentYearStart() {
      const now = new Date();
      return formatLocalDate(new Date(now.getFullYear(), 0, 1));
    },
    currentYearEnd() {
      const now = new Date();
      return formatLocalDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
    }
  },
  
  methods: {
    formatNumber(value) {
      return value.toLocaleString();
    },
    fetchData() {
      axios.get('/api/v1/invoice-counts/').then((response) => {
        this.thisYearValidatedCount = response.data.this_year_validated_count;
        this.thisYearValidatedOnlyCount = response.data.this_year_validated_only_count;
        this.thisYearLhdnNotSuccessfulCount = response.data.this_year_lhdn_not_successful_count;
        this.thisYearPendingApprovalCount = response.data.this_year_pending_approval_count;
        this.thisMonthValidatedCount = response.data.this_month_validated_count;
        this.thisMonthValidatedOnlyCount = response.data.this_month_validated_only_count;
        this.thisMonthLhdnNotSuccessfulCount = response.data.this_month_lhdn_not_successful_count;
        this.thisMonthPendingApprovalCount = response.data.this_month_pending_approval_count;
      });
      axios.get('/api/v1/invoices-over-months/').then((response) => {
        this.invoicesOverMonths = response.data;
      });
      axios.get('/api/v1/top-buyers/').then((response) => {
        this.topBuyers = response.data;
      });
      axios.get('/api/v1/invoices-by-sender/').then((response) => {
        this.invoicesBySender = response.data;
      });
    },
    fetchMessages() {
      axios.get('/api/v1/notifications/messages/').then((response) => {
        this.messages = response.data;
        if (this.messages.length > 0) {
          this.showModal = true;
        }
      });
    },
    dismissMessage() {
      const messageId = this.currentMessage.id;
      axios
        .post('/api/v1/notifications/messages/dismiss/', { message_id: messageId })
        .then(() => {
          this.messages.splice(this.currentIndex, 1);
          if (this.messages.length === 0) {
            this.showModal = false;
          } else if (this.currentIndex >= this.messages.length) {
            this.currentIndex = 0;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    nextMessage() {
      this.currentIndex += 1;
    },
    closeModal() {
      this.showModal = false;
    },
  },
  mounted() {
    this.fetchData();
    this.fetchMessages();
  },
  
};
</script>

<style scoped>
.user-dashboard {
  padding: 20px;
}

.card-content {
  cursor: pointer;
}

.equal-height {
  height: 90%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.has-background-grey-darker {
  background-color: #2e2e2e;
}

.has-text-white {
  color: #ffffff;
}

.tabs ul li.is-active a {
  color: #3273dc;
  border-bottom-color: #3273dc;
}
</style>
